import React, { useState, useEffect } from 'react';
import ItemHeader from '../organisms/ItemHeader';
import ItemGallery from '../organisms/ItemGallery';
import ItemInfo from '../organisms/ItemInfo';
import VenueProperties from '../organisms/VenueProperties';
import ItemReviews from '../organisms/ItemReviews';
import ItemRatings from '../organisms/ItemRatings';
import VenueSpaces from '../organisms/VenueSpaces';
import VenueLocations from '../organisms/VenueLocations';
import VenueProviders from '../organisms/VenueProviders';
import ItemMedia from '../organisms/ItemMedia';
import ItemContacts from '../organisms/ItemContacts';
import ItemFooter from '../organisms/ItemFooter';
import { display } from '../../data/display';
import { imageSizes } from '../../data/constants';
import { convertImageIDToURL } from '../../lib/apis/image';
import { Venue } from '@prisma/client';
import VenuesList from '../organisms/VenuesList';
import { useApp } from '../../context/AppContext';

interface VenueDetailsProps {
  venue: Venue;
  otherVenues?: any[];
  access?: boolean;
  claimed?: boolean;
  claim?: () => void;
  setLayer: (el: any) => void;
  setFullLayer: (el: any) => void;
  referer?: string;
  serviceProviderPickHandler?: (data: any) => void;
  serviceProviderRemoveHandler?: (data: any) => void;
  isVenueAdmin: boolean;
  serviceProviders?: any[];
  venueSpaceEditHandler?: (data: { id: string; lexorank: string }) => void;
}

const VenueDetails = (props: VenueDetailsProps) => {
  const { devMode } = useApp();
  const itemType = 'venue';
  const [currData, setCurrData] = useState({}) as any;
  const {
    venue,
    access,
    claim,
    claimed,
    setLayer,
    setFullLayer,
    referer,
    serviceProviders,
    otherVenues,
    isVenueAdmin,
  } = props;

  /*
   * General Data
   */
  const {
    id,
    slug,
    plan,
    files,
    headline,
    description,
    location,
    contact_details,
  } = currData;

  const planFields = (() => {
    switch (true) {
      case plan === 'BASIC':
        return display.venue.basic;
      case plan === 'PLATINUM':
      case plan === 'TRIAL_PLATINUM':
      case Boolean(access):
      default:
        return display.venue.platinum;
    }
  })();

  /*
   * Info Data
   */
  const address = location ? location.address : {};
  const currInfoData = {
    slug,
    plan,
    headline,
    description,
    ...location,
    ...contact_details,
    ...address,
  };

  /*
   * Properties Data
   */
  const tmpObj = {
    ...currData,
    map: (files || []).filter((el: any) => el.category === 'venue-map')[0],
  };

  delete tmpObj.id;
  delete tmpObj.files;
  delete tmpObj.name;
  delete tmpObj.slug;
  delete tmpObj.description;
  delete tmpObj.contact_details;
  delete tmpObj.sales_contacts;
  delete tmpObj.management_contacts;
  delete tmpObj.venue_spaces;
  delete tmpObj.reviews;
  const currPropertiesData = tmpObj;

  /*
   * Spaces Data
   */
  const currSpacesData = (currData.venue_spaces || []).map((space: any) => {
    return {
      ...space,
      images: (space.images || []).map((el: any) => {
        return {
          ...el,
          hidden: plan !== 'PLATINUM' && plan !== 'TRIAL_PLATINUM',
          small: convertImageIDToURL(el.image_id, imageSizes.s),
          large: convertImageIDToURL(el.image_id, imageSizes.xxl),
        };
      }),
      floorplan:
        space.floorplan ||
        (files || []).filter(
          (el: any) => el.category.indexOf(`floorplan-${space.id}`) > -1
        )[0],
    };
  });

  /*
   * Locations
   */
  const { parking, hotels, transportation } = currData;
  const currLocationsData = { location, parking, hotels, transportation };

  /*
   * Service Provider
   */
  const currProvidersData =
    venue.plan === 'BASIC' && !access ? [] : currData.service_providers || [];

  /*
   * Media & documents
   */
  const currMediaData = !!planFields.media
    ? (files || []).filter(
        (el: any) =>
          el.category !== 'venue-map' &&
          el.category?.indexOf('floorplan') === -1
      )
    : [];

  /*
   * Contacts
   */
  const currContactsData = planFields.contacts
    ? [
        ...(currData.sales_contacts || []),
        ...(currData.management_contacts || []),
      ].map((el: any) => {
        const { image_id } = el.image || {};
        return !!image_id
          ? {
              ...el,
              image: {
                ...el.image,
                url: convertImageIDToURL(image_id, imageSizes.logo),
              },
            }
          : el;
      })
    : [];

  /*
   * Edit Data Callback
   */
  const apply = () => {
    setLayer(null);
  };

  /**
   *  Update current data on venueState change
   */
  useEffect(() => {
    const data = { ...venue };
    setCurrData(data);
  }, [venue]);

  return (
    <>
      {/* Header, Title, Location */}
      <ItemHeader
        type={itemType}
        data={currData}
        access={access}
        claimed={claimed}
        claim={claim}
        referer={referer}
        isVenueAdmin={isVenueAdmin}
      />
      <br />
      {/* Gallery */}
      <ItemGallery venueID={id} data={currData} access={access} />

      {/* Headline, Description, Contact details */}
      <ItemInfo
        venueID={id}
        data={currInfoData}
        access={access}
        apply={apply}
      />
      <br />
      <br />
      {/* Venue Information: Properties */}
      <VenueProperties
        venueID={id}
        venuePlan={plan}
        data={currPropertiesData}
        access={access}
        apply={apply}
        setLayer={setLayer}
      />

      {/* Star Rating */}
      <ItemRatings
        type={itemType}
        data={currData}
        access={access}
        apply={apply}
        setLayer={setLayer}
      />

      {/* Reviews */}
      <ItemReviews
        type={itemType}
        data={currData}
        access={access}
        apply={apply}
        setLayer={setLayer}
      />

      {/* Spaces: Halls & Rooms */}
      <VenueSpaces
        venueID={id}
        venuePlan={plan}
        data={currSpacesData}
        access={access}
        apply={apply}
        setLayer={setLayer}
        setFullLayer={setFullLayer}
        venueSpaceEditHandler={props.venueSpaceEditHandler}
      />

      {/* Location Info / Map  */}
      <VenueLocations
        venueID={id}
        data={currLocationsData}
        access={access}
        setLayer={setLayer}
        apply={apply}
      />

      {/* Service Providers */}
      <VenueProviders
        data={currProvidersData}
        access={access}
        apply={apply}
        setLayer={setLayer}
        serviceProviderPickHandler={props.serviceProviderPickHandler}
        serviceProviderRemoveHandler={props.serviceProviderRemoveHandler}
        serviceProviders={serviceProviders}
      />

      {/* Media & documents */}
      <ItemMedia
        venueID={id}
        data={currMediaData}
        access={access}
        apply={apply}
        setLayer={setLayer}
      />

      {/* Contact */}
      <ItemContacts
        venueID={id}
        data={currContactsData}
        access={access}
        apply={apply}
        setLayer={setLayer}
      />

      <ItemFooter
        data={currData}
        type={itemType}
        access={access}
        claim={claim}
        setLayer={setLayer}
      />

      {/* Other venues */}
      {devMode && !!otherVenues?.length && <VenuesList data={otherVenues} />}
    </>
  );
};

export default VenueDetails;
